import { type FeedbackProps } from 'pages/session/feedback/Feedback'
import {
  type Problem,
  type GuideNode,
  type TocEntity,
  type TreelessDiagnostics,
  HasFormConfig,
  ChatAnswerFeedback,
} from 'tsp-types'
export type FormSubmitStatus =
  | 'pending'
  | 'init_process'
  | 'processing'
  | 'done'
  | 'failure'
export enum ProblemStatus {
  RESOLVED = 'resolved',
  UNRESOLVED = 'unresolved',
  IGNORED = 'ignored',
}
export const ProblemStatusConfig = {
  [ProblemStatus.RESOLVED]: {
    label: 'treeless.problemStatus.resolved',
  },
  [ProblemStatus.UNRESOLVED]: {
    label: 'treeless.problemStatus.unresolved',
  },
  [ProblemStatus.IGNORED]: {
    label: 'treeless.problemStatus.ignored',
  },
}
export interface NavigationCurrentPosition {
  guideId: string
  guideName: string
  nodeId: string
  nodeType: string
  refId: string | null
}

/** `unresolvedNode`: the guide moved to a node whose content is not loaded. */
export type StepError = 'unresolvedNode'

export interface Session {
  data: Record<string, TocEntity>
  step?: Step
  status: STATUS
  view: SessionSectionType | ViewType
  answer: string
  formValues: Record<string, any>
  claimDetails?: Record<string, any>
  isFormNodeValid?: boolean
  formSubmitStatus?: FormSubmitStatus
  formSubmitError?: { error: string; userMessage: string } | null
  feedback: FeedbackProps | null
  /** Set when the guide cannot open the step it navigated to. */
  stepError?: StepError | null
  meta: Record<string, any>
  isTreelessWindow: boolean
  mtime?: number
  syncTime?: number
  isAiChatOpen?: boolean
  aiChatState?: AIChatState
}

export interface AIChatMessage {
  id: string
  message: string
  from: 'user' | 'assistant'
  feedback?: ChatAnswerFeedback
  hasError?: boolean
  metadata?: Record<string, any>
}

export interface AIChatState {
  _id: string
  messages: AIChatMessage[]
}

export interface TraverseHistory {
  guideId: string
  nodeId: string
  nodeRef?: string
  parentGuideId: string
  action?: string
}

export interface NavigationState {
  currentPosition: NavigationCurrentPosition | null
  navigationStack: NavigationStackEntry[]
  startingGuideId: string
  timestamp: number
}
export interface NavigationStackEntry {
  guideId: string
  guideName: string
  entryNodeId: string
  depth: number
  calledFromGuide?: string
  calledFromNode?: string
  calledFromGuideNodeId?: string // Track the specific guide node instance
  // New fields for tracking all transitions
  previousNodeId?: string
  viaEdgeId?: string
  viaSourceHandle?: string
  transitionType: 'guide_entry' | 'regular_move' | 'initial'
  // New fields for sub-guide completion tracking
  subGuideCompleted?: boolean
  exitedViaNode?: string
  fromCompletedSubGuide?: string
  previousGuideId?: string
}

export interface TreelessSession extends Session {
  type: 'TREELESS'
  problems: Record<string, Problem>
  treelessData?: TreelessDiagnostics
  status: STATUS
  db: string
}

export interface TreelessWarranty {
  testIds: string[]
  repairId: string
  warrantyId: string
  formValues: Record<string, any>
  steps: Step[]
  status?: 'initiated' | 'submitted' | 'pending'
}
export interface Step extends GuideNode, HasFormConfig {
  meta?: {
    guideId?: string
    nodeId?: string
    path?: StepPath[]
    guideName?: string
    stepCount?: number
    traverseHistory?: TraverseHistory[]
    navigationState?: NavigationState
    formValues?: Record<string, any> | null
    formSubmitStatus?: FormSubmitStatus
  }
}

export interface StepPath {
  guideId: string
  nodeId: string
  nodeRef: string
  answerId: string
  parentGuideId?: string
  formValues?: Record<string, any> | null
  claimDetails?: Record<string, any>
  formSubmitStatus?: FormSubmitStatus
}

export enum SessionSectionType {
  GUIDE = 'Guide',
  SOLUTIONS = 'Solutions',
  HISTORY = 'History',
  DETAILS = 'Details',
}
export const { GUIDE, SOLUTIONS, HISTORY, DETAILS } = SessionSectionType

export enum SessionSectionTypeLocal {
  GUIDE_LOCALE = 'session.tabList.guide',
  SOLUTIONS_LOCALE = 'session.tabList.solutions',
  HISTORY_LOCALE = 'session.tabList.history',
  DETAILS_LOCALE = 'session.tabList.details',
}
export const {
  GUIDE_LOCALE,
  SOLUTIONS_LOCALE,
  HISTORY_LOCALE,
  DETAILS_LOCALE,
} = SessionSectionTypeLocal

export enum ViewType {
  FEEDBACK = 'Feedback',
  SUMMARY = 'Summary',
  REPAIR_WARRANTY_CLAIM = 'WarrantyClaim',
}

export type STATUS =
  | 'IN_PROGRESS'
  | 'PAUSED'
  | 'ESCALATED'
  | 'TERMINATED'
  | 'COMPLETED'

export enum SessionStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  PAUSED = 'PAUSED',
  ESCALATED = 'ESCALATED',
  TERMINATED = 'TERMINATED',
  COMPLETED = 'COMPLETED',
}

/**
 * Sessions started in the browser get a local uuid and only live in IndexedDB.
 * Backend history endpoints reject anything that is not a Mongo id, so callers
 * must check before posting.
 */
export function isBackendSessionId(sessionId?: string | null): boolean {
  return !!sessionId && /^[0-9a-f]{24}$/i.test(sessionId)
}

/** Finished sessions reopen on Summary with guide tabs disabled. */
export function isFinishedSessionStatus(
  status: STATUS | SessionStatus | string | undefined
): boolean {
  return (
    status === SessionStatus.COMPLETED || status === SessionStatus.ESCALATED
  )
}

export const { FEEDBACK, SUMMARY, REPAIR_WARRANTY_CLAIM } = ViewType
